import { Link, useParams, Navigate } from 'react-router-dom';
import { Eyebrow, SectionHeader } from '@/components/Atoms';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { getServiceBySlug, SERVICES } from '@/data/services';
import { SERVICE_FAQ_MAP, GENERAL_FAQ } from '@/data/faqs';
import { SERVICES as SERVICES_TID } from '@/constants/testIds';
import { buildServiceSchema, buildFAQSchema } from '@/data/schema';
import useSEO from '@/hooks/useSEO';

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);
  const serviceFaqs = (SERVICE_FAQ_MAP[slug] || []).concat(GENERAL_FAQ.slice(0, 2));

  useSEO({
    title: service ? `${service.name}` : 'Service',
    description: service?.summary,
    image: service?.image,
    path: `/services/${slug}`,
    schema: service
      ? {
          '@context': 'https://schema.org',
          '@graph': [buildServiceSchema(service), buildFAQSchema(serviceFaqs)],
        }
      : undefined,
  });

  if (!service) return <Navigate to="/services" replace />;

  return (
    <div>
      {/* HERO */}
      <section data-testid={SERVICES_TID.detailHero} className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-7">
            <Eyebrow>{service.eyebrow}</Eyebrow>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-[68px] leading-[1.02] tracking-tight mt-6">{service.headline}</h1>
            <p className="mt-8 text-lg text-[#1C1C1C]/80 leading-relaxed max-w-xl">{service.summary}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-testid={SERVICES_TID.detailCta}>Book a Discovery Call</Link>
              <Link to="/contact" className="btn-ghost" data-testid={`${SERVICES_TID.detailCta}-consult`}>Request a Consultation</Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={service.image} alt={service.name} className="w-full h-full object-cover editorial-img" />
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-[#FBF7F2] border-y border-[#E6CFC7]/70 py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <SectionHeader eyebrow="Benefits" title="What changes" italic="when this is handled well." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8">
            {service.benefits.map((b, i) => (
              <div key={i} className="flex gap-6 items-start">
                <div className="num-marker shrink-0">{String(i + 1).padStart(2, '0')}</div>
                <p className="text-lg text-[#1C1C1C]/85 leading-relaxed pt-2">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DELIVERABLES */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Eyebrow>Deliverables</Eyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-5 leading-[1.05] tracking-tight">
              Exactly what<br/><span className="font-serif-italic italic text-[#C9A24D]">you can expect.</span>
            </h2>
          </div>
          <div className="lg:col-span-8 lg:pt-3">
            <ul className="divide-y divide-[#E6CFC7]/60">
              {service.deliverables.map((d, i) => (
                <li key={i} className="py-5 flex items-start gap-5">
                  <span className="text-xs uppercase tracking-[0.18em] text-[#C9A24D] pt-1.5 w-10 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                  <span className="text-lg text-[#1C1C1C]/85">{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-[#1C1C1C] text-[#F4EFEA] py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Eyebrow>Process</Eyebrow>
          <h2 className="font-serif-display text-4xl sm:text-5xl mt-5 leading-[1.05] tracking-tight max-w-3xl">
            How we work<br/><span className="font-serif-italic italic text-[#C9A24D]">together.</span>
          </h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#F4EFEA]/15">
            {service.process.map((p) => (
              <div key={p.step} className="bg-[#1C1C1C] p-8 lg:p-10">
                <div className="num-marker text-[#C9A24D]">{p.step}</div>
                <h3 className="font-serif-display text-2xl mt-6">{p.title}</h3>
                <p className="text-sm text-[#E6CFC7]/85 leading-relaxed mt-3">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — per service */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-16">
          <SectionHeader eyebrow={`${service.name} FAQ`} title="Common questions" italic="about this service." />
          <div className="mt-14">
            <Accordion type="single" collapsible className="border-t border-[#E6CFC7]/70">
              {serviceFaqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b border-[#E6CFC7]/70" data-testid={`${SERVICES_TID.faqItem}-${i}`}>
                  <AccordionTrigger className="text-left text-lg sm:text-xl font-serif-display hover:no-underline py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#1C1C1C]/80 leading-relaxed text-base pb-6 pr-8">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1C1C1C] text-[#F4EFEA] py-24 lg:py-32">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 text-center">
          <Eyebrow className="mx-auto">Begin</Eyebrow>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] tracking-tight">
            Ready to make {service.name.toLowerCase()}<br/><span className="font-serif-italic italic text-[#C9A24D]">a strength of your business?</span>
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-ghost-light" data-testid={`${SERVICES_TID.detailCta}-bottom`}>Book a Discovery Call</Link>
            <Link to="/portfolio" className="text-sm uppercase tracking-[0.18em] text-[#F4EFEA]/70 hover:text-[#C9A24D] self-center transition-colors" data-testid={`${SERVICES_TID.detailCta}-portfolio`}>View Portfolio &rarr;</Link>
          </div>
        </div>
      </section>

      {/* OTHER SERVICES */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <SectionHeader eyebrow="Continue" title="Other ways" italic="we might work together." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {others.map((o) => (
              <Link key={o.slug} to={`/services/${o.slug}`} className="card-soft p-8 block group">
                <div className="eyebrow">{o.eyebrow}</div>
                <h3 className="font-serif-display text-2xl mt-5 group-hover:text-[#C9A24D] transition-colors">{o.name}</h3>
                <p className="mt-3 text-sm text-[#1C1C1C]/70 leading-relaxed">{o.short}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
